import { createAsyncThunk } from "@reduxjs/toolkit";
import { getProductsKroger, getProductDetailsKroger, getKrogerStoreLocation, getGuestProperty } from "../../utils/kroger";
import request from "../../utils/request";
import shuffle from 'lodash/shuffle';
import excludedProductIds from "../../constants/ExcludedProductIds";
import { setLoadingFromCache } from "../slices/guestSlices";
import {
    getProductSearchCacheKey,
    readProductSearchCache,
    writeProductSearchCache,
    findLoadedCategoryProducts,
    normalizeProductSearchTerm,
} from "../../utils/localProductSearch";

const krogerCache = (() => {
    let cache = null;
    let lastUpdated = null;

    return {
        get: () => cache,
        set: (data) => {
            cache = data;
            lastUpdated = Date.now();
        },
        isValid: () => {
            return lastUpdated !== null && Date.now() - lastUpdated < 30 * 60 * 1000; // 30 minutes in milliseconds
        },
    };
})();

export const getKrogerToken = async () => {
    if (krogerCache.isValid()) {
        // if the cache is still valid, return the cached data
        return krogerCache.get();
    } else {
        // otherwise, fetch the access token from /kroger/ and update the cache
        const { data } = await request.get("/kroger/");
        krogerCache.set(data);
        return krogerCache.get();
    }
}

const getProducts = createAsyncThunk(
    "products/getProducts",
    async (params, { rejectWithValue, getState }) => {
        const company = localStorage.getItem('activeCompany');
        const companyData = company ? JSON.parse(company) : null;
        const companyName = companyData?.name || "";

        const term = params.term ? params.term : 'Food';
        const start = Number(params?.start) || 0;
        const limit = Number(params?.limit) || 48;
        const normalizedTerm = normalizeProductSearchTerm(term);
        const cacheKey = getProductSearchCacheKey(
            normalizedTerm,
            start,
            limit,
            companyName
        );

        const cached = readProductSearchCache(cacheKey);
        if (cached) {
            return { ...cached, fromCache: true };
        }

        // Reuse an already-loaded category shelf for matching first-page searches.
        if (start === 0 && companyName) {
            const categoryHit = findLoadedCategoryProducts({
                companyName,
                categoryProductsState: getState()?.multipleCategoryProducts,
                term: normalizedTerm,
            });
            if (categoryHit?.products?.length) {
                const payload = {
                    products: categoryHit.products.slice(0, limit),
                    total: categoryHit.total || categoryHit.products.length,
                    fromCache: true,
                    fromCategoryCache: true,
                };
                writeProductSearchCache(cacheKey, {
                    products: payload.products,
                    total: payload.total,
                });
                return payload;
            }
        }

        try {
            let products, total;
            if (term) {
                let resp = await getProductsKroger(term, start, limit, companyData);
                console.log('kroger response => ', resp)
                products = resp.data;
                total = resp.meta?.pagination.total || 0;
            }
            const payload = { products, total };
            writeProductSearchCache(cacheKey, payload);
            return payload;

        } catch (err) {
            console.log(err.message);
            return rejectWithValue(err.response?.data || { error: err.message });
        }
    }
);

// Helper function to add delay between requests
const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

// Helper function to fetch with retry and rate limiting
const fetchWithRetry = async (category, start, end, activeCompany, retries = 3, delayMs = 500) => {
    for (let i = 0; i < retries; i++) {
        try {
            return await getProductsKroger(category, start, end, activeCompany);
        } catch (err) {
            // If 429 (rate limit), wait longer before retry
            if (err.response?.status === 429) {
                const waitTime = delayMs * Math.pow(2, i); // Exponential backoff
                console.warn(`Rate limited for category ${category}, waiting ${waitTime}ms before retry ${i + 1}/${retries}`);
                await delay(waitTime);
                continue;
            }
            // For other errors, throw immediately
            throw err;
        }
    }
    throw new Error(`Failed to fetch category ${category} after ${retries} retries`);
};

const getMultipleCategoryProducts = createAsyncThunk(
    "productDetails/getMultipleCategoryProducts",
    async (params, { rejectWithValue, getState }) => {
        const { customterms, start, end, oneCategory = false, activeCompany } = params;
        const terms = Array.isArray(customterms) ? customterms : [];
        const startIndex = Number(start) || 0;

        const state = getState();
        const companyName = activeCompany?.name;
        const companyState = companyName
            ? state.multipleCategoryProducts?.[companyName]
            : null;

        const readCategoryProducts = (categoryName, sourceState = companyState) => {
            const catState = sourceState?.[categoryName];
            if (!catState || !Array.isArray(catState.products) || !catState.products.length) {
                return null;
            }
            return {
                products: catState.products,
                total: catState.total || catState.products.length,
            };
        };

        const buildCachedPayload = (sourceState) => {
            const cachedProducts = terms.map((category) => {
                return readCategoryProducts(category, sourceState)?.products || [];
            });
            const cachedTotals = terms.map((category) => {
                return readCategoryProducts(category, sourceState)?.total || 0;
            });
            const allCached = terms.length > 0 && cachedProducts.every((list) => list.length > 0);
            if (!allCached) return null;
            return {
                products: oneCategory ? cachedProducts[0] : cachedProducts,
                totals: oneCategory ? [cachedTotals[0]] : cachedTotals,
                fromCache: true,
            };
        };

        // Full cache hit from Redux (initial page only)
        if (companyName && companyState && startIndex === 0) {
            const cachedPayload = buildCachedPayload(companyState);
            if (cachedPayload) {
                return cachedPayload;
            }
        }

        // Full cache hit from localStorage hydration
        if (companyName && typeof window !== 'undefined' && startIndex === 0) {
            try {
                const stored = localStorage.getItem("multipleCategoryProducts");
                if (stored) {
                    const parsed = JSON.parse(stored);
                    if (parsed?.[companyName]) {
                        const cachedPayload = buildCachedPayload(parsed[companyName]);
                        if (cachedPayload) {
                            return cachedPayload;
                        }
                    }
                }
            } catch (err) {
                // Silently fail, will fetch from API
            }
        }

        try {
            const property = localStorage.getItem("property") && JSON.parse(localStorage.getItem("property"))
            if (property) {
                let products = null;
                let totals = null;

                if (oneCategory) {
                    // Prefer Redux, then localStorage for first-page lazy shelves
                    if (startIndex === 0) {
                        const cached = readCategoryProducts(terms[0]);
                        if (cached) {
                            return {
                                products: cached.products,
                                totals: [cached.total],
                                fromCache: true,
                            };
                        }
                        try {
                            const stored = localStorage.getItem("multipleCategoryProducts");
                            if (stored && companyName) {
                                const parsed = JSON.parse(stored);
                                const storedHit = readCategoryProducts(
                                    terms[0],
                                    parsed?.[companyName]
                                );
                                if (storedHit) {
                                    return {
                                        products: storedHit.products,
                                        totals: [storedHit.total],
                                        fromCache: true,
                                    };
                                }
                            }
                        } catch (err) {
                            // fall through to API
                        }
                    }
                    const result = await fetchWithRetry(terms[0], start, end, activeCompany);
                    products = result.data;
                    const total =
                        result.meta?.pagination?.total ||
                        (Array.isArray(products) ? products.length : 0);
                    // start === 0 replaces; pagination (start > 0) appends
                    return {
                        products,
                        totals: startIndex === 0 ? [total] : null,
                    };
                } else {
                    // Only fetch missing categories; reuse already-loaded shelves
                    products = [];
                    totals = [];

                    for (let i = 0; i < terms.length; i++) {
                        const category = terms[i];
                        let cached = startIndex === 0 ? readCategoryProducts(category) : null;
                        if (!cached && startIndex === 0 && companyName) {
                            try {
                                const stored = localStorage.getItem("multipleCategoryProducts");
                                if (stored) {
                                    const parsed = JSON.parse(stored);
                                    cached = readCategoryProducts(
                                        category,
                                        parsed?.[companyName]
                                    );
                                }
                            } catch (err) {
                                // fall through to API
                            }
                        }
                        if (cached) {
                            products.push(cached.products);
                            totals.push(cached.total);
                            continue;
                        }

                        try {
                            const result = await fetchWithRetry(category, start, end, activeCompany);
                            products.push(result.data);
                            totals.push(result.meta?.pagination.total || 0);

                            if (i < terms.length - 1) {
                                await delay(300);
                            }
                        } catch (err) {
                            console.error(`Error fetching category ${category}:`, err);
                            products.push([]);
                            totals.push(0);
                        }
                    }

                    return { products, totals };
                }
            }
        } catch (err) {
            console.log(err)
            if (err.request) {
                return rejectWithValue({ error: "No response from the server, please try again later" });
            }
            return rejectWithValue(err.response?.data || { error: err.message });
        }
    }
)

const getProductDetails = createAsyncThunk(
    "productDetails/getProductDetails",
    async (id, { rejectWithValue, getState }) => {
        // Check cache first to avoid duplicate API calls
        const state = getState();
        const cache = state.productDetails?.cache || {};
        
        // Check cache by request ID first
        if (cache[id]) {
            return cache[id];
        }
        
        // Also check cache by productId, _id, or upc in case the request ID differs
        for (const cachedProduct of Object.values(cache)) {
            const cachedId = cachedProduct?.productId || cachedProduct?._id || cachedProduct?.upc;
            if (cachedId === id) {
                return cachedProduct;
            }
        }
        
        // Fallback: Check localStorage if Redux cache is empty (async hydration)
        if (typeof window !== 'undefined') {
            try {
                const stored = localStorage.getItem("productDetailsCache");
                if (stored) {
                    const parsed = JSON.parse(stored);
                    // Check by request ID
                    if (parsed[id]) {
                        return parsed[id];
                    }
                    // Check by productId, _id, or upc
                    for (const cachedProduct of Object.values(parsed)) {
                        const cachedId = cachedProduct?.productId || cachedProduct?._id || cachedProduct?.upc;
                        if (cachedId === id) {
                            return cachedProduct;
                        }
                    }
                }
            } catch (err) {
                // Silently fail, will fetch from API
            }
        }

        try {
            // const data = await getKrogerToken()
            if (id.length === 24) {
                // Check if this product ID is excluded from MongoDB fetches
                if (excludedProductIds.includes(id)) {
                    return rejectWithValue({ 
                        error: "This product is no longer available from the database. Please use Kroger API." 
                    });
                }
                
                const company = localStorage.getItem('activeCompany');
                const companyData = JSON.parse(company);
                if (!companyData || companyData?.name == 'Ralphs') {
                    const { data: { data: product } } = await request.get(`/products/${id}`);
                    return product;
                } else if (companyData && companyData.name != 'Ralphs') {
                    const { data } = await request.get(`/company/products-details/${id}`)
                    return data;
                }
            } else {
                const product = getProductDetailsKroger(id);
                return product;
            }
        } catch (err) {
            if (err.request) {
                return rejectWithValue({ error: "No response from the server, please try again later" });
            }
            return rejectWithValue(err.response.data);
        }
    }
);

const getRelatedProducts = createAsyncThunk(
    "relatedProducts/getRelatedProducts",
    async (params, { rejectWithValue }) => {

        // destructuring function parameters
        const term = params?.term;
        const start = params?.start;
        const company = localStorage.getItem('activeCompany');
        const companyData = JSON.parse(company);

        try {
            // const data = await getKrogerToken()

            let products = await getProductsKroger(term, start, 10, companyData);
            products = products.data;

            return products;
        } catch (err) {
            return rejectWithValue(err.response.data);
        }
    }
);

const getPackages = createAsyncThunk(
    "packages/getPackages",
    async (id, { rejectWithValue }) => {

        try {
            // getting saved packages from the DB
            const { data } = await request.get('/packages/active')

            return data.packages;
        } catch (err) {
            return rejectWithValue(err.response.data);
        }
    }
);

const getOrderCompletedImages = createAsyncThunk(
    "OrderCompletedImages/getOrderCompletedImages",
    async (id, { rejectWithValue }) => {

        try {

            const { data } = await request.get(`/orders/images/${id}`);
            return data.signedUrls;

        } catch (err) {
            if (err.request) {
                return rejectWithValue({ error: "No response from the server, please try again later" });
            }
            return rejectWithValue(err.response.data);
        }
    }
);

const getGuestLocationId = createAsyncThunk(
    "GuestLocationId/getGuestLocationId",
    async (params, { rejectWithValue }) => {
        try {
            const id = await getGuestProperty(params);
            return id;
        } catch (err) {
            return rejectWithValue(err.response.data);
        }
    }
);


export { getProducts, getProductDetails, getRelatedProducts, getPackages, getMultipleCategoryProducts, getOrderCompletedImages, getGuestLocationId }
